import React from 'react';
import { uniqueModeEmojis } from '../../../shared/transport';
import { ceilHours, ceilEurosFromCents, euroRangeLabel, formatEuroExact, stayCostStateOf } from '../utils';
import Flags from './Flags';
import { nightRidesLabel } from '../nightTrain';

// Icon-led, self-describing chips: a stays pill ("🛏️ 6 stays in 🇵🇹 costing
// €600" — user-entered stay prices, exact and unstarred) + one aggregated
// transport pill (design-handoff.md B.3 SummaryChips; stays grammar mirrors
// the transport pill's, Paulo 2026-08-18). Shared by the Trip-detail
// RouteFocus and the Unsorted rows, so it takes a minimal structural shape
// that both the AlternativeSummary and the OrphanSummary satisfy.

export interface RouteSummary {
  stopsCount: number;
  countrySet: string[];
  transportModes: string[];
  movingTimeHours: number;
  costCents: number | null;
  /** Price-band range; when present it replaces the midpoint display. */
  costLowCents?: number | null;
  costHighCents?: number | null;
  costCurrency?: string;
  /** Stay-cost rollup (see utils.StayCostSummary); optional so orphan
   *  summaries without stop data keep rendering a count-only pill. */
  stayCostCents?: number | null;
  staysPriced?: number;
  staysTotal?: number;
  /** Night trains (spec §7.6): the rides, and the moving time without them; absent on orphan summaries. */
  nightRides?: number;
  dayTimeHours?: number;
}

const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
  padding: '5px 10px',
  borderRadius: 999,
  background: '#f4f6f3',
  fontSize: 13,
  color: '#16241d',
  whiteSpace: 'nowrap',
};

const SummaryChips: React.FC<{ summary: RouteSummary; invitePricing?: boolean }> = ({ summary, invitePricing = true }) => {
  // Approximate, rounded-up figures (see ApproxNote). The * marks them. The
  // cost shows the price-band RANGE when available (midpoint as fallback).
  // With night trains the moving time is split into rides and hours by day
  // (Paulo 2026-09-30): "2 night train rides + 9h*", not one big number.
  const rides = summary.nightRides || 0;
  const hours = ceilHours(rides > 0 && summary.dayTimeHours != null ? summary.dayTimeHours : summary.movingTimeHours);
  const costLabel = summary.costLowCents != null && summary.costHighCents != null
    ? euroRangeLabel(summary.costLowCents, summary.costHighCents)
    : summary.costCents != null ? `€${ceilEurosFromCents(summary.costCents)}` : null;

  // Stay-cost pill states (Paulo 2026-08-18): complete → the exact sum;
  // partial → sum with a trailing "+" ("at least this" — Paulo 2026-09-12,
  // matching AlternativeJourneyChip) + "(2 of 5 priced)" as a progress nudge;
  // none → an invite. User-entered values, so NO star. The costing segment is
  // omitted when the route has no overnight stay (nothing to price) or no
  // rollup data. `invitePricing={false}` (read-only shared views) drops the
  // "add prices" invite: a visitor can't price anything.
  const stays = { sumCents: summary.stayCostCents ?? null, staysPriced: summary.staysPriced ?? 0, staysTotal: summary.staysTotal ?? 0 };
  const staysState = stayCostStateOf(stays);
  const showStayCost = summary.staysTotal != null && (stays.staysTotal > 0 || stays.sumCents != null)
    && !(staysState === 'none' && !invitePricing);

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      <span style={chipStyle}>
        🛏️ <b>{summary.stopsCount} {summary.stopsCount === 1 ? 'stay' : 'stays'}</b>
        {summary.countrySet.length > 0 && (
          <>
            <span style={{ color: '#6c7872' }}>in</span>
            <Flags countries={summary.countrySet} />
          </>
        )}
        {showStayCost && (
          <>
            <span style={{ color: '#6c7872' }}>costing</span>
            {staysState === 'none'
              ? <span style={{ color: '#6c7872' }}>€? (add prices in each stay)</span>
              : (
                <>
                  <b>{formatEuroExact(stays.sumCents!)}{staysState === 'partial' ? '+' : ''}</b>
                  {staysState === 'partial' && <span style={{ color: '#6c7872' }}>({stays.staysPriced} of {stays.staysTotal} priced)</span>}
                </>
              )}
          </>
        )}
      </span>

      {/* One aggregated transport pill: "Xh* moving by <emojis> costing €Y*",
          or "🌙 2 night train rides + 9h* by <emojis> costing €Y*". */}
      <span style={chipStyle}>
        {rides > 0 ? <>🌙 {nightRidesLabel(rides)}{hours > 0 ? ` + ${hours}h*` : ''}</> : <>🕒 {hours}h* moving</>}
        {summary.transportModes.length > 0 && (
          <>
            {' by '}
            <span style={{ display: 'inline-flex', gap: 2 }}>
              {uniqueModeEmojis(summary.transportModes).map((m) => (
                <span key={m.emoji} title={m.label}>{m.emoji}</span>
              ))}
            </span>
          </>
        )}
        {costLabel != null && ` costing ${costLabel}*`}
      </span>
    </div>
  );
};

export default SummaryChips;
